import {
	DeleteOutlined,
	DownloadOutlined,
	ReloadOutlined,
} from '@ant-design/icons';
import { Button, Input, Popconfirm, Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useMemo, useState } from 'react';
import { LimitationDto, LimitationType } from '../../../../api-client';
import { limitationTypeTag } from '../../../../utils/enumUtils';

type BVLimitationsTableProps = {
	loading: boolean;
	limitations: LimitationDto[];
	onRefresh: (sapCode?: string) => void | Promise<void>;
	onRemove: (limitation: LimitationDto) => void | Promise<void>;
	loadingRemove: boolean;
	onExport: () => void | Promise<void>;
	loadingExport: boolean;
};

type GroupedLimitations = {
	sapCode: string;
	limitations: LimitationDto[];
};

const BVLimitationsTable = ({
	loading,
	limitations,
	onRefresh,
	onRemove,
	loadingRemove,
	onExport,
	loadingExport,
}: BVLimitationsTableProps) => {
	const [sapCodeFilter, setSapCodeFilter] = useState<string>('');

	const typeTag = (t?: LimitationType) => {
		var tagInfo = limitationTypeTag(t);
		return <Tag color={tagInfo.color}>{tagInfo.typeString}</Tag>;
	};
	useEffect(() => {
		onRefresh(undefined);
	}, []);

	const grouped: GroupedLimitations[] = useMemo(() => {
		const list = limitations ?? [];
		const map = new Map<string, LimitationDto[]>();

		for (const l of list) {
			const sc = (l.sapCode ?? '').trim() || '-';
			if (!map.has(sc)) map.set(sc, []);
			map.get(sc)!.push(l);
		}

		return Array.from(map.entries())
			.sort(([a], [b]) => a.localeCompare(b))
			.map(([sapCode, items]) => ({
				sapCode,
				limitations: items,
			}));
	}, [limitations]);

	const filteredGrouped = useMemo(() => {
		const q = sapCodeFilter.trim().toUpperCase();
		if (!q) return grouped;
		return grouped.filter((g) => g.sapCode.toUpperCase().includes(q));
	}, [grouped, sapCodeFilter]);

	const handleRefresh = async () => {
		const q = sapCodeFilter.trim();
		await onRefresh(q ? q : undefined);
	};

	const masterColumns: ColumnsType<GroupedLimitations> = useMemo(
		() => [
			{
				title: 'SAP Code',
				dataIndex: 'sapCode',
				key: 'sapCode',
				render: (v: string) => v || '-',
			},
			{
				title: 'Limitations',
				key: 'count',
				width: 130,
				align: 'right',
				render: (_v, r) => r.limitations.length,
			},
		],
		[],
	);

	const detailColumns: ColumnsType<LimitationDto> = useMemo(
		() => [
			{
				title: 'Type',
				dataIndex: 'type',
				key: 'type',
				width: 220,
				render: (v: LimitationType) => typeTag(v),
			},
			{
				title: 'Value',
				dataIndex: 'value',
				key: 'value',
				render: (v?: string) => v || '-',
			},
			{
				title: '',
				key: 'actions',
				width: 90,
				align: 'right',
				render: (_v, r) => (
					<Popconfirm
						title="Remove limitation?"
						okText="Remove"
						cancelText="Cancel"
						onConfirm={async () => {
							await onRemove(r);
							await handleRefresh();
						}}
					>
						<Button
							icon={<DeleteOutlined />}
							danger
							size="small"
							loading={loadingRemove}
						/>
					</Popconfirm>
				),
			},
		],
		[loadingRemove, onRemove],
	);

	const expandedRowRender = (record: GroupedLimitations) => (
		<Table<LimitationDto>
			rowKey={(r) => `${r.sapCode}-${r.type}-${r.value}`}
			columns={detailColumns}
			dataSource={record.limitations}
			pagination={false}
			size="small"
		/>
	);

	return (
		<div className="admin-card" style={{ marginTop: 16 }}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					gap: 12,
					flexWrap: 'wrap',
				}}
			>
				<label className="admin-label">Limitations</label>

				<div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
					<Input
						style={{ minWidth: 320 }}
						placeholder="Filter by SAP code (optional)"
						value={sapCodeFilter}
						allowClear
						onChange={(e) => setSapCodeFilter(e.target.value)}
						onPressEnter={handleRefresh}
					/>

					<Button
						icon={<ReloadOutlined />}
						onClick={handleRefresh}
						loading={loading}
					>
						Refresh
					</Button>

					<Button
						icon={<DownloadOutlined />}
						onClick={onExport}
						loading={loadingExport}
					>
						Download
					</Button>
				</div>
			</div>

			<Table<GroupedLimitations>
				style={{ marginTop: 12 }}
				rowKey={(r) => r.sapCode}
				loading={loading}
				dataSource={filteredGrouped}
				columns={masterColumns}
				pagination={{ pageSize: 20 }}
				expandable={{
					expandedRowRender,
					rowExpandable: (r) => (r.limitations?.length ?? 0) > 0,
				}}
			/>
		</div>
	);
};

export default BVLimitationsTable;
